import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { makeStyles } from '@material-ui/core/styles';
import { MuiThemeProvider, Typography } from '@material-ui/core';
import { axuminTheme, darkGrey, universalStyles, white } from '../../../../app/theme';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import CaseStudyRow from '../../../micro/caseStudy/CaseStudyRow';
import CASE_STUDY, { CaseStudyFormat } from '../../../../constants/caseStudy.constants';
import Indication from '../../../micro/Indication/AxuminIndication';
import TrainingFooter from '../../../micro/TrainingFooter/TrainingFooter';
import TrainingNavBar from '../../../micro/TrainingNavBar/AxuminTrainingNavBar';
import AxuminNavBar from '../../../micro/BrandNavBar/AxuminNavBar';
import ExternalLinkModal from '../../../micro/ExternalLinkModal/ExternalLinkModal';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  background: {
    backgroundColor: darkGrey,
    width: '100%',
    margin: 0,
    padding: '40px 0 56px',
  },
  content: {
    maxWidth: 1242,
    margin: 'auto',
  },
  yellowHeader: {
    margin: '20px 0',
    lineHeight: 1.2,
  },
  headerRow: {
    marginTop: 40,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  header: {
    color: white,
    fontSize: 24,
  },
  icon: {
    width: 35,
    height: 'auto',
    marginRight: 15,
  },
  whiteText: {
    color: white,
  },
  caseStudy: {
    marginBottom: 30,
    '&:last-of-type': {
      margin: 0,
    },
  },
  footnoteBox: {
    backgroundColor: darkGrey,
    paddingTop: 49,
    paddingBottom: 50,
    paddingLeft: 64,
    paddingRight: 64,
  },
  footnotes: {
    marginTop: 32,
  },
  footnote: {
    fontSize: 14,
    letterSpacing: 0,
    lineHeight: 1.42,
    color: white,
    '& p': {
      margin: 0,
    },
    '& a': {
      color: 'inherit',
      textDecoration: 'none',
    },
    '& ol': {
      paddingLeft: 16,
      margin: 0,
    },
    '& sup': {
      verticalAlign: 'top',
      fontSize: 'smaller',
    },
  },
  urlFootnote: {
    display: 'inline',
    fontSize: 14,
    letterSpacing: 0,
    lineHeight: 1.42,
    color: white,
    '& p': {
      margin: 0,
      display: 'inline',
    },
  },
  url: {
    display: 'inline',
    fontSize: 14,
    letterSpacing: 0,
    lineHeight: 1.42,
    color: white,
    cursor: 'pointer',
  },
}));

const AxuminAdditionalCaseStudies = (): JSX.Element => {
  const classes = useStyles();
  const caseStudyData = (CASE_STUDY.additional[0] as CaseStudyFormat[]) || [];
  const [modalOpen, setModalOpen] = useState(false);
  const footnotes = [
    '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup>  (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
    '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
  ];
  const parseForExternalLinks = (note: string) => {
    if (note.indexOf('<a') >= 0) {
      const start = note.indexOf('<a');
      const end = note.indexOf('</a>') + 4;
      const href = note.split('href="')[1].split('"')[0];
      let number = '';
      let firstString = note.substring(0, start);
      const split = note.substring(0, start).split('. ')[0];
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(Number(split))) {
        number = `${split}. `;
        firstString = firstString.substring(split.length + 2);
      }
      return (
        <span className={classes.urlFootnote}>
          <span>{number}</span>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{firstString}</ReactMarkdown>
          <Typography className={classes.url} onClick={() => setModalOpen(true)}>
            {href}
          </Typography>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{note.substring(end)}</ReactMarkdown>
          <ExternalLinkModal url={href} isOpen={modalOpen} handleModalOpen={(value) => setModalOpen(value)} />
        </span>
      );
    }
    return <ReactMarkdown rehypePlugins={[rehypeRaw]}>{note}</ReactMarkdown>;
  };

  useEffect(() => {
    document.title = 'Additional Case Studies';
  }, []);

  return (
    <MuiThemeProvider theme={axuminTheme}>
      <TrainingNavBar />
      <AxuminNavBar section="Home" />
      <div className={classes.background}>
        <div className={classes.content}>
          <Typography variant="h1">Additional Cases</Typography>
          <Typography variant="h6" className={classes.yellowHeader}>
            Please click on each of the additional case examples below to learn more about evaluating a patient who has
            been administered Axumin.
          </Typography>
          <Typography variant="body1" className={classes.whiteText}>
            <b>Disclaimer: </b>
            These case studies are provided as examples of the interpretation and utility of Axumin
            <sup>®</sup>
            &nbsp;(fluciclovine F 18) injection PET/CT imaging results. Axumin is not indicated for directing or
            changing patient management. Such decisions must be based on the independent medical judgment of the
            physician(s) responsible for the individual patient’s management. Does not include all clinical information
            needed to assess the case.
          </Typography>
          <div className={classes.headerRow}>
            <img
              className={classes.icon}
              src={`${BASE_URL}${IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_ICON.path}`}
              alt={IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_ICON.alt}
            />
            <Typography className={classes.header}>Case Studies</Typography>
          </div>
          <div className={classes.grayBox}>
            {caseStudyData.map((_study, index) => (
              // Not a good value to use as a key, so using the index
              // eslint-disable-next-line react/no-array-index-key
              <div className={classes.caseStudy} key={index}>
                <CaseStudyRow trainingId={1} module="additional" page={0} index={index} />
              </div>
            ))}
          </div>
        </div>
        <div className={classes.footnoteBox}>
          {footnotes.length > 0 ? (
            <div className={classes.footnotes}>
              {footnotes.map((note) => (
                <div key={note} className={classes.footnote}>
                  {parseForExternalLinks(note)}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <Indication />
      <TrainingFooter />
    </MuiThemeProvider>
  );
};

export default AxuminAdditionalCaseStudies;
